/**
 * The plan's SIXTH source: the **wind and temperature aloft** on each leg —
 * `GET /api/wx/winds`, written into the route block's W/V column.
 *
 * Same shape as `route.ts`, `aircraft.ts`, `wx.ts` and `geo.ts`: everything above
 * the `--- the network step ---` divider is a pure function over the plan's own
 * values, so the rules are pinned down by tests instead of by clicking, and the
 * store owns *when* it runs and which cells it may touch (`PlanStore.setWinds`,
 * source `'winds'`).
 *
 * What it fills, and nothing else: `leg<N>_wind`, one cell per drawn leg. The
 * temperature comes back too and is shown beside the leg on the map — the LBA
 * form has no cell for it, and inventing one would be a form the LBA did not
 * print.
 *
 * Five decisions worth writing down.
 *
 * **The point is the leg's MIDPOINT, at the leg's altitude.** A wind read at the
 * departure aerodrome is the wind at the departure aerodrome; the number the wind
 * triangle wants is the average over the leg, and the midpoint is the cheapest
 * honest stand-in for it. `alt_ft` is `parseAltFt(leg<N>_alt)` — the SAME parse
 * the airspace check uses (`route.ts`), so the wind and the airspace answer
 * describe one altitude and cannot drift apart: `"3500"` is 3 500 ft, `"FL 65"`
 * is 6 500 ft, `"3.5"` is 3 500 ft (this form writes thousands with a decimal
 * point), and anything else falls back to the waypoints' own planned altitude and
 * then to {@link DEFAULT_WIND_ALT_FT} — the airspace check's default, again for
 * one number rather than two. It is clamped to the endpoint's 0..20 000 ft.
 *
 * **The time is when that leg is actually flown**: the plan's Datum at ETD, plus
 * the leg times so far, plus half of this leg's own — the midpoint of a leg is
 * flown halfway through it. A leg whose time the calculator has not produced yet
 * (it cannot, before there is a wind) contributes the zero-wind estimate from its
 * own distance and TAS instead of nothing, so the FIRST pass already asks the
 * right hours (see {@link legMins}). A plan whose Datum/ETD is
 * missing, already past, or beyond the forecast horizon uses **now** and says so
 * ({@link WindPlan.now}) rather than asking for a time the endpoint answers 422 to.
 *
 * **One request per forecast hour, at most {@link MAX_WIND_REQUESTS}.** The
 * endpoint takes ONE `at` for the whole batch and snaps it to the nearest hour,
 * so legs that fall in the same hour are one question. A two-hour flight is two
 * questions and gets the later legs' later forecast, which is the whole point of
 * carrying the leg times around; a flight long enough to need a fourth hour has
 * its tail folded into the third, because this is a planning aid and not a
 * briefing.
 *
 * **It fetches by itself, on a schedule, not on a drag.** There is no „Wind
 * laden" any more: the forecast is a fact about the route and the clock, so
 * `windsContext.tsx` asks again whenever one of the two moves. What it watches is
 * {@link windsSignature} — the leg midpoints, the leg altitudes and the departure
 * HOUR, and deliberately not `leg<N>_time`, which the answer itself changes — and
 * a whole gesture is one question, because a 1.5 s debounce covers a drag. An
 * hour-old answer is re-read when the pilot comes back to the tab, and the ⟳ in
 * the section header forces it in between. That is what keeps a twelve-leg route
 * at one request: the budget is spent on answers somebody reads.
 *
 * A fetched value can still quietly stop describing its leg, because the pilot
 * may edit while a request is out, so the fetch stores a FINGERPRINT of the leg
 * midpoints it was made for (`meta.winds.fp`) and the map marks a leg whose
 * midpoint has since moved as **veraltet** — the value is kept, because last
 * hour's wind at the old midpoint is still a better number than an empty cell,
 * but it stops looking fresh.
 *
 * **The alternate row is skipped.** `alt_wind` has no leg on the map — the
 * alternate is one aerodrome cell, not a pair of waypoints — so there is no
 * midpoint to ask about and the row stays the pilot's.
 */

import { legMinutes, midpoint, num, parseHM } from '@avia/core';

import * as api from './api';
import { LEG_NUMBERS, legField, parseDateParts, type LegNumber } from './fields';
import { DEFAULT_CHECK_ALT_FT, MAX_ROUTE_LEGS, parseAltFt, type Waypoint } from './route';
import { type PlanStore, type WindsStatus } from './store';
import { zHour } from './wx';

/** The endpoint's own attribution string; the fallback when a stored plan has none. */
export const WINDS_SOURCE = 'Open-Meteo (CC BY 4.0) · DWD ICON';

/** `GET /api/wx/winds` takes 1..24 points and 0..20 000 ft (`server/api/src/winds.rs`). */
export const MAX_WIND_POINTS = 24;
export const MAX_WIND_ALT_FT = 20000;

/**
 * How many forecast hours one pass may ask about. Each is a request, and each
 * request is up to three upstream calls, so this is the bound that keeps a
 * twelve-leg route from being twelve of them.
 */
export const MAX_WIND_REQUESTS = 3;

/** The altitude a leg with no Höhe/FL is asked about — the airspace check's default. */
export const DEFAULT_WIND_ALT_FT = DEFAULT_CHECK_ALT_FT;

/** The endpoint's window for `at`: `now - 2 h` .. `now + 5 d`, and a 422 outside it. */
const WINDOW_BACK_MS = 2 * 3600_000;
const WINDOW_AHEAD_MS = 5 * 24 * 3600_000;
/** Kept off both ends of that window, so the round trip cannot land on the wrong side of it. */
const WINDOW_MARGIN_MS = 60_000;

const HOUR_MS = 3600_000;

/* ================================================================== *
 * what to ask, and when
 * ================================================================== */

/** The altitude one leg is asked about, in feet, already inside the endpoint's range. */
export function legAltFt(raw: string, from: number | null, to: number | null): number {
  const parsed = parseAltFt(raw);
  const planned = from != null && to != null ? (from + to) / 2 : from ?? to;
  const v = parsed ?? planned ?? DEFAULT_WIND_ALT_FT;
  return Math.max(0, Math.min(MAX_WIND_ALT_FT, Math.round(v)));
}

/**
 * How long a leg takes, for the purpose of deciding WHICH forecast hour it is
 * flown in.
 *
 * `leg<N>_time` is the calculator's own answer and is used whenever it is there —
 * but it only exists once a wind does: `computeLeg` derives GS from the wind
 * triangle and the time from GS, so on a plan that has never been fetched EVERY
 * leg time is empty. Scheduling from that put a two-hour flight's last leg on the
 * departure hour's forecast, and only a second pass (with the times now on the
 * form) split it correctly — which, on a schedule that only re-reads when the
 * route or the departure hour moves, would never come.
 *
 * So a leg with no time falls back to the ZERO-WIND estimate from its own
 * distance and TAS, both of which are on the form before any wind is: the route
 * sync writes `leg<N>_dist`, the aircraft profile (or the pilot) writes
 * `leg<N>_tas`. It is the same number the triangle would produce in still air —
 * wrong by a few minutes, which is far inside the hour this is choosing.
 */
export function legMins(val: (id: string) => string, leg: LegNumber): number {
  const t = num(val(legField(leg, 'time')));
  if (Number.isFinite(t) && t > 0) return t;
  const dist = num(val(legField(leg, 'dist')));
  const tas = num(val(legField(leg, 'tas')));
  if (!Number.isFinite(dist) || dist <= 0 || !Number.isFinite(tas) || tas <= 0) return 0;
  const est = legMinutes(dist, tas);
  return est != null && Number.isFinite(est) && est > 0 ? est : 0;
}

/** ISO-8601 with whole seconds — RFC 3339 as the endpoint parses it. */
export const isoAt = (ms: number): string => new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');

/**
 * Why a plan's own Datum/ETD could not be used, when it could not.
 *
 * The two cases read as opposite things to a pilot and must not share a sentence:
 * `'unset'` is a form that is not filled in yet, `'outside'` is a form that IS —
 * a flight more than five days out, or a plan reopened after its departure time.
 * Telling the second „Datum und Startzeit fehlen" is simply false.
 */
export type StartFallback = 'unset' | 'outside';

/**
 * When the flight starts, for the purpose of asking about a forecast.
 *
 * `fallback` names why the plan's own Datum/ETD could not be used at all, and is
 * null when it could. The pilot is told; the alternative is a 422 with nothing on
 * the screen to explain it.
 */
export function planStart(
  datum: string, etd: string, now: Date,
): { at: Date; fallback: StartFallback | null } {
  const p = parseDateParts(datum);
  // core's own "HH:MM"/"HHMM" parser, so ETD is read exactly as the calculator reads it
  const etdMin = parseHM(etd);
  if (!p || !Number.isFinite(etdMin)) return { at: now, fallback: 'unset' };
  const ms = Date.UTC(p.y, p.mo - 1, p.d) + etdMin * 60_000;
  if (!Number.isFinite(ms)) return { at: now, fallback: 'unset' };
  const lo = now.getTime() - WINDOW_BACK_MS + WINDOW_MARGIN_MS;
  const hi = now.getTime() + WINDOW_AHEAD_MS - WINDOW_MARGIN_MS;
  if (ms < lo || ms > hi) return { at: now, fallback: 'outside' };
  return { at: new Date(ms), fallback: null };
}

/** Keep a time inside the window the endpoint accepts — a long route can run past it. */
const clampAt = (ms: number, now: Date): number =>
  Math.min(
    now.getTime() + WINDOW_AHEAD_MS - WINDOW_MARGIN_MS,
    Math.max(now.getTime() - WINDOW_BACK_MS + WINDOW_MARGIN_MS, ms),
  );

/** One question: where a leg's midpoint is, how high, and when it is flown. */
export interface WindPoint {
  /** the form's leg number, 1-based (`leg<N>_wind`) */
  leg: LegNumber;
  lat: number;
  lon: number;
  alt_ft: number;
  /** ISO-8601: when this leg's midpoint is flown */
  at: string;
}

export interface WindPlan {
  points: WindPoint[];
  /** the plan's Datum/ETD was unusable — these winds are for NOW (see {@link planStart}) */
  now: StartFallback | null;
  /** legs the route has that neither the form nor the endpoint can carry */
  dropped: number;
}

/**
 * Everything one route asks the forecast, in leg order.
 *
 * `val` is the plan's own reader (`store.val`), so this stays a pure function of
 * the document: the waypoints for the geometry, `leg<N>_alt` for the altitude,
 * `leg<N>_time` for when, `datum`/`etd` for where the clock starts.
 */
export function windPlan(
  waypoints: readonly Waypoint[],
  val: (id: string) => string,
  now: Date,
): WindPlan {
  const start = planStart(val('datum'), val('etd'), now);
  const legs = Math.max(0, waypoints.length - 1);
  const used = Math.min(legs, MAX_ROUTE_LEGS, MAX_WIND_POINTS);
  const points: WindPoint[] = [];
  /** minutes flown BEFORE the current leg */
  let flown = 0;
  for (let i = 0; i < used; i++) {
    const leg = LEG_NUMBERS[i];
    const a = waypoints[i];
    const b = waypoints[i + 1];
    if (leg === undefined || !a || !b) continue;
    const mins = legMins(val, leg);
    const mid = midpoint({ lat: a.lat, lon: a.lon }, { lat: b.lat, lon: b.lon });
    points.push({
      leg,
      lat: mid.lat,
      lon: mid.lon,
      alt_ft: legAltFt(val(legField(leg, 'alt')), a.alt_ft, b.alt_ft),
      at: isoAt(clampAt(start.at.getTime() + (flown + mins / 2) * 60_000, now)),
    });
    flown += mins;
  }
  return { points, now: start.fallback, dropped: Math.max(0, legs - used) };
}

/* ---------------- grouping into requests ---------------- */

export interface WindRequest {
  /** the whole-hour `at` this batch asks about */
  at: string;
  points: WindPoint[];
}

/** The hour the endpoint would snap a time to — it takes the NEAREST one, not the floor. */
export const windHour = (at: string): number => Math.round(Date.parse(at) / HOUR_MS) * HOUR_MS;

/**
 * Legs grouped into requests, one per forecast hour.
 *
 * Points arrive in leg order and time only moves forward, so the groups come out
 * in order too. Beyond {@link MAX_WIND_REQUESTS} hours the remaining legs join the
 * last group rather than opening a fourth: they then carry a forecast that is
 * early for them, which is what a planning aid may do and a briefing may not.
 */
export function windRequests(points: readonly WindPoint[]): WindRequest[] {
  const byHour = new Map<number, WindPoint[]>();
  for (const p of points) {
    const h = windHour(p.at);
    const own = byHour.get(h);
    if (own) {
      own.push(p);
      continue;
    }
    if (byHour.size >= MAX_WIND_REQUESTS) {
      const groups = [...byHour.values()];
      groups[groups.length - 1]?.push(p);
      continue;
    }
    byHour.set(h, [p]);
  }
  return [...byHour].map(([h, list]) => ({ at: isoAt(h), points: list }));
}

/* ---------------- the fingerprint that ages a value ---------------- */

/** What one leg's wind was fetched FOR: its midpoint (to ~100 m) and its altitude. */
export const legPrint = (p: WindPoint): string =>
  String(p.leg) + ':' + p.lat.toFixed(3) + ',' + p.lon.toFixed(3) + ',' + String(p.alt_ft);

export const windFingerprint = (points: readonly WindPoint[]): string => points.map(legPrint).join(';');

/**
 * What a fetch would ask about, as one comparable string — the thing the
 * unattended schedule watches.
 *
 * Two parts, and only two: the fingerprint (every leg's midpoint and altitude)
 * and the HOUR the flight starts in. The per-leg times are deliberately absent,
 * although the request carries them: a fetched wind changes GS and therefore
 * every leg time, so a signature that read those would ask again for its own
 * answer, and again for that one's. The departure hour moves when the pilot
 * edits Datum/ETD. It does NOT walk with the clock on a plan that has neither:
 * `windsContext` recomputes this per document revision, so a plan nobody is
 * editing keeps asking the same question — which is right, and the hour-old
 * answer on such a plan is re-read by the schedule's own staleness check.
 *
 * `''` means there is nothing to ask about, and cancels rather than fetches.
 */
export function windsSignature(
  waypoints: readonly Waypoint[],
  val: (id: string) => string,
  now: Date,
): string {
  const plan = windPlan(waypoints, val, now);
  if (plan.points.length === 0) return '';
  const start = planStart(val('datum'), val('etd'), now);
  return windFingerprint(plan.points) + '|' + String(windHour(isoAt(start.at.getTime())));
}

/**
 * Legs whose midpoint or altitude has moved since the fetch `fp` describes.
 *
 * A leg the fingerprint does not mention is NOT stale — it was never fetched, so
 * there is no value of ours in it to have aged. A leg that has since disappeared
 * cannot be reported here either, and does not need to be: `setWinds` clears it.
 */
export function staleLegs(fp: string | undefined, points: readonly WindPoint[]): Set<number> {
  const out = new Set<number>();
  if (!fp) return out;
  const was = new Map<number, string>();
  for (const part of fp.split(';')) {
    const n = Number(part.split(':')[0]);
    if (Number.isInteger(n)) was.set(n, part);
  }
  for (const p of points) {
    const w = was.get(p.leg);
    if (w !== undefined && w !== legPrint(p)) out.add(p.leg);
  }
  return out;
}

/* ---------------- one leg's answer ---------------- */

/** What the forecast says about one leg. `wv` absent = the endpoint could not answer. */
export interface WindLeg {
  leg: number;
  /** `"314/3"` — written into `leg<N>_wind` verbatim, the server owns that format */
  wv?: string;
  temp_c?: number;
  /** `icon_d2` | `icon_eu` | `best_match` */
  model?: string;
  /** the forecast hour served, e.g. `2026-08-17T12:00Z` */
  hour?: string;
  /** `'unavailable'` */
  error?: string;
}

/** "+17 °C", "-3 °C", "0 °C" — whole degrees, because the leg line is one line. */
export function fmtTempC(c: number): string {
  const n = Math.round(c) || 0;
  return (n > 0 ? '+' : '') + String(n) + ' °C';
}

/**
 * The one-line summary the map shows beside a leg: `W/V 314/3 · +17 °C · 12Z`.
 * Each part is dropped when it is not known, so a plan reloaded from storage
 * (which keeps the W/V cell but not the temperature) still reads.
 *
 * The hour is the one the forecast is FOR, and it is the last thing on the line
 * that a pilot can act on. The model behind it used to be printed here too; it
 * is now on the sources page, where a name nobody flies by belongs.
 */
export function windLegText(l: WindLeg | undefined | null, wv?: string): string {
  const parts: string[] = [];
  const v = l?.wv ?? wv ?? '';
  if (v) parts.push('W/V ' + v);
  if (l?.temp_c !== undefined) parts.push(fmtTempC(l.temp_c));
  const hour = zHour(l?.hour);
  if (hour) parts.push(hour);
  return parts.join(' · ');
}

/* ---------------- what goes into the form ---------------- */

/**
 * The W/V cells this fetch has something to say about.
 *
 * Three cases, and `setWinds` reads all three (see its doc): a VALUE for a leg
 * that was answered, `''` for a leg the route no longer has (the cell is given
 * back), and NOTHING AT ALL for a leg that exists but could not be answered —
 * the pilot's own number, or the previous fetch's, stays on the form.
 */
export function windValues(legs: number, results: ReadonlyMap<number, WindLeg>): Map<string, string> {
  const out = new Map<string, string>();
  for (const j of LEG_NUMBERS) {
    const r = results.get(j);
    if (r?.wv) {
      out.set(legField(j, 'wind'), r.wv);
      continue;
    }
    if (j > legs) out.set(legField(j, 'wind'), '');
  }
  return out;
}

/* ================================================================== *
 * --- the network step ---
 * ================================================================== */

export interface WindsLoad {
  /** leg number → what the forecast said; only the legs that were asked about */
  legs: Map<number, WindLeg>;
  status: WindsStatus;
  /** why the plan's own Datum/ETD was unusable, when it was — these winds are for NOW */
  now: StartFallback | null;
  /** legs beyond the twelve the form holds (or the endpoint's 24 points) */
  dropped: number;
  /** legs the forecast could not answer for — their cells were left alone */
  unavailable: number[];
  /** legs whose W/V the pilot owns; the answer was thrown away rather than overwrite it */
  kept: number[];
  /** cells actually changed */
  written: number;
  /** how many requests it took (one per forecast hour) */
  requests: number;
}

/** The majority model across the answered points; the response's own is the tie-break. */
function bestModel(legs: ReadonlyMap<number, WindLeg>, fallback: string): string {
  const tally = new Map<string, number>();
  for (const l of legs.values()) {
    if (!l.model) continue;
    tally.set(l.model, (tally.get(l.model) ?? 0) + 1);
  }
  let best = fallback;
  let n = 0;
  for (const [m, c] of tally) {
    if (c > n) {
      n = c;
      best = m;
    }
  }
  return best;
}

/**
 * Fetch the winds for the plan's legs and write them into the form — the one
 * impure function here, and the only one that knows about `PlanStore`.
 *
 * `null` when the plan has no leg to ask about; the caller says so. The requests
 * run concurrently: there are at most three of them and the pilot is waiting.
 */
export async function loadWinds(
  store: PlanStore,
  opts: { signal?: AbortSignal; now?: Date } = {},
): Promise<WindsLoad | null> {
  const now = opts.now ?? new Date();
  const waypoints = store.route?.waypoints ?? [];
  const plan = windPlan(waypoints, id => store.val(id), now);
  if (plan.points.length === 0) return null;

  const requests = windRequests(plan.points);
  const answers = await Promise.all(
    requests.map(r =>
      api.fetchWinds(
        r.points.map(p => ({ lat: p.lat, lon: p.lon, alt_ft: p.alt_ft })),
        r.at,
        opts.signal,
      ),
    ),
  );

  const legs = new Map<number, WindLeg>();
  const unavailable: number[] = [];
  requests.forEach((req, i) => {
    const res = answers[i];
    req.points.forEach((p, k) => {
      // the endpoint answers point-for-point, in order (`winds.rs`)
      const a = res?.points[k];
      const leg: WindLeg = { leg: p.leg };
      if (a?.wv) {
        leg.wv = a.wv;
        if (typeof a.temp_c === 'number') leg.temp_c = a.temp_c;
        if (a.model) leg.model = a.model;
        if (a.source_hour) leg.hour = a.source_hour;
      } else {
        leg.error = a?.error ?? 'unavailable';
        unavailable.push(p.leg);
      }
      legs.set(p.leg, leg);
    });
  });

  // A cell the pilot filled by hand is never overwritten (the store enforces it),
  // but saying nothing about it would look like the forecast had no answer.
  const kept: number[] = [];
  for (const l of legs.values()) {
    if (!l.wv) continue;
    const id = legField(l.leg as LegNumber, 'wind');
    if (store.val(id) !== '' && store.derivedSource(id) !== 'winds') kept.push(l.leg);
  }

  const first = answers[0];
  const hour = [...legs.values()].find(l => l.hour)?.hour;
  // every hour that was actually served, in order — a fetch may span up to three,
  // and a single `hour` on a per-cell tooltip would be leg 1's claim about leg 8
  const hours = [...new Set([...legs.values()].map(l => l.hour).filter((h): h is string => !!h))];
  const status: WindsStatus = {
    fetched_at: first?.fetched_at ?? isoAt(now.getTime()),
    model: bestModel(legs, first?.model ?? ''),
    at: requests[0]?.at ?? isoAt(now.getTime()),
    ...(hour ? { hour } : {}),
    ...(hours.length > 1 ? { hours } : {}),
    source: first?.source || WINDS_SOURCE,
    fp: windFingerprint(plan.points),
  };

  const written = store.setWinds(windValues(plan.points.length, legs), status);

  return {
    legs,
    status,
    now: plan.now,
    dropped: plan.dropped,
    unavailable,
    kept,
    written,
    requests: requests.length,
  };
}
